import { useMutation } from "@tanstack/react-query"
import { trpc } from "app/trpc"
import styled from "@emotion/styled"
import { FaCaretDown } from "@react-icons/all-files/fa/FaCaretDown"
import { Value } from "@stringtale/react"
import { SUPPORT_LINKS } from "app/core/consts"
import { useCurrentOrganization } from "app/core/hooks/useCurrentOrganization"
import { useOrganizatorRoleFromContext } from "app/core/providers/UserInfoProvider"
import { addQueryParams } from "app/core/utils/addQueryParams"
import Avatar from "app/files/components/Avatar"
import { MODAL_QUERY_ARCHIVE_KEY } from "app/playlists/consts"
import { useHasArchiveYears } from "app/schooljaren/hooks/useHasArchiveYears"
import { useHasSchoolyears } from "app/schooljaren/hooks/useHasSchoolyears"
import type { GetCurrentOrganizationResult } from "app/users/queries/getCurrentOrganization"
import type { Group } from "db/client"
import Link from "next/link"
import { parseAsString, useQueryState } from "nuqs"
import { usePostHog } from "posthog-js/react"
import { LIGHT_GREY_COLOR, PX14 } from "theme/consts"
import Button from "ui/Button"
import ContextMenu from "ui/ContextMenu"
import Text from "ui/Text"
import { File } from "utils/files/types"
import getFullname from "utils/users/getFullName"
import { useRouter } from "app/core/hooks/useRouter"
import { useLogout } from "app/auth/hooks/useLogout"

type Props = {
  user: {
    id: number
    firstName: string | null
    insertion: string | null
    lastName: string | null
    avatar: File | null
    role: string
  }
  groups: Group[] | null
  group?: {
    id: number
    displayTitle: string
  } | null
  className?: string
  hideArchive?: boolean
}

const Root = styled(Button)`
  display: flex;
  padding: 0 28px;
  align-items: center;
  gap: 14px;
  /* border-right: ${LIGHT_GREY_COLOR} 1px solid; */
  border-left: ${LIGHT_GREY_COLOR} 1px solid;
  cursor: pointer;
`
const UserContent = styled.div`
  @media screen and (max-width: 1280px) {
    display: none;
  }
`
const UserName = styled.div`
  font-size: ${PX14};
  font-weight: 600;
  line-height: 1.5;
`
const UserGroup = styled.div`
  font-size: ${PX14};
  line-height: 1;
`

export default function HeaderUser({
  user,
  group,
  groups,
  className,
  hideArchive = false,
}: Props) {
  const logout = useLogout()
  const organizatorRole = useOrganizatorRoleFromContext()
  const archiveYearsPresent = useHasArchiveYears()
  const hasSchoolyears = useHasSchoolyears()
  const [open, setOpen] = useQueryState(MODAL_QUERY_ARCHIVE_KEY)
  const [next] = useQueryState("next", parseAsString)
  const organization = useCurrentOrganization()

  return (
    <ContextMenu
      trigger={
        <Root className={className}>
          <Avatar user={user} />
          <UserContent>
            <UserName data-hj-suppress>{getFullname(user)}</UserName>
            {group && <UserGroup>{group.displayTitle}</UserGroup>}
          </UserContent>
          <FaCaretDown />
        </Root>
      }
      items={
        <>
          <ContextMenu.Label>
            <Value name="apps.web.src.header.components.headeruser.account">
              Account
            </Value>
          </ContextMenu.Label>
          <ContextMenu.Separator />
          <ContextMenu.Item asChild>
            <a
              href="https://123zing.my.site.com/knowledgebase/s/contactsupport"
              target="_blank"
              rel="noreferrer"
            >
              <Value name="apps.web.src.header.components.headeruser.klantenservice_contact">
                Klantenservice & contact
              </Value>
            </a>
          </ContextMenu.Item>
          <ContextMenu.Item asChild>
            <a
              href={SUPPORT_LINKS["GENERAL_SUPPORT"]}
              target="_blank"
              rel="noreferrer"
            >
              <Value
                name="apps.web.src.header.components.headeruser.hulp_en_aan_de_slag"
                version="1"
              >
                Hulp & Aan de slag
              </Value>
            </a>
          </ContextMenu.Item>
          {user.role !== "KID" && user.role !== "STUDENT" && (
            <ContextMenu.Item asChild>
              <Link href="/instellingen">
                <Value name="apps.web.src.header.components.headeruser.profiel_instellingen">
                  Profiel & Instellingen
                </Value>
                {/*<Value name="apps.web.src.header.components.headeruser.123zing_plan">
                </Value>*/}
              </Link>
            </ContextMenu.Item>
          )}
          <ContextMenu.Separator />
          {organizatorRole && organizatorRole !== "MEMBER" ? (
            <ContextMenu.Item asChild>
              <Link href="/schoolbeheer">
                {organizatorRole === "MANAGER"
                  ? organization?.subscription
                      .managerSchoolManagementUserMenuTitle || (
                      <Value name="apps.web.src.header.components.headeruser.schoolbeheer">
                        Schoolbeheer
                      </Value>
                    )
                  : organization?.subscription
                      .initiatorSchoolManagementUserMenuTitle || (
                      <Value name="apps.web.src.header.components.headeruser.schoolbeheer">
                        Schoolbeheer
                      </Value>
                    )}
              </Link>
            </ContextMenu.Item>
          ) : null}
          {organizatorRole && organizatorRole !== "MEMBER" ? (
            <>
              <ContextMenu.Item asChild>
                <Link href="/schoolbeheer/plan">
                  <Value name="apps.web.src.header.components.headeruser.123zing_plan">
                    123ZING Plan
                  </Value>
                </Link>
              </ContextMenu.Item>
            </>
          ) : null}
          {organizatorRole && organizatorRole === "MEMBER" ? (
            <ContextMenu.Item asChild>
              <Link href="/mijnschool">
                {organization?.subscription
                  .memberSchoolManagementUserMenuTitle || (
                  <Value name="apps.web.src.header.components.headeruser.mijn_school">
                    Mijn school
                  </Value>
                )}
              </Link>
            </ContextMenu.Item>
          ) : null}
          {groups && groups.length > 1 ? (
            <ContextMenu.Item asChild>
              <Link href={addQueryParams("/groepskeuze", next ? { next } : {})}>
                <Value name="apps.web.src.header.components.headeruser.wissel_groep">
                  Wissel groep
                </Value>
              </Link>
            </ContextMenu.Item>
          ) : null}
          {hasSchoolyears && archiveYearsPresent && !hideArchive ? (
            <ContextMenu.Item asChild>
              <Text onClick={() => setOpen("")}>
                <Value name="apps.web.src.header.components.headeruser.archief">
                  Archief
                </Value>
              </Text>
            </ContextMenu.Item>
          ) : null}
          {organizatorRole ? <ContextMenu.Separator /> : null}
          <ContextMenu.Item
            onClick={async () => {
              await logout()
            }}
          >
            <Value name="apps.web.src.header.components.headeruser.uitloggen">
              Uitloggen
            </Value>
          </ContextMenu.Item>
        </>
      }
    />
  )
}
